import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import { DateRangePicker } from "react-date-range";
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import Modal from 'react-modal';
import Select from 'react-select';
import { useGlobalContext } from "../context/globalContext";
import Loading from "../components/Loading";

const API_ENDPOINT = "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/call";

// Custom modal styles
const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        padding: '20px',
        borderRadius: '10px'
    }
};

export default function CallScore() {
    const { userInfo } = useGlobalContext();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState('all');
    const [qualityScores, setQualityScores] = useState({});
    const [agents, setAgents] = useState([]);
    const [selectionRange, setSelectionRange] = useState({
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection'
    });

    const calculateScores = (totalCalls, totalDuration) => {
        const avgCallsPerWeek = Math.round(totalCalls / 7);
        const avgTalkTime = Math.round(totalDuration / 7);

        let callScore = 0;
        if (avgCallsPerWeek >= 115 && avgCallsPerWeek <= 125) {
            callScore = 1;
        } else if (avgCallsPerWeek >= 126 && avgCallsPerWeek <= 150) {
            callScore = 2;
        } else if (avgCallsPerWeek > 150) {
            callScore = 3;
        }

        let talkTimeScore = 0;
        if (avgTalkTime >= 5400 && avgTalkTime <= 7200) {
            talkTimeScore = 1;
        } else if (avgTalkTime > 7200 && avgTalkTime <= 9000) {
            talkTimeScore = 2;
        } else if (avgTalkTime > 9000) {
            talkTimeScore = 3;
        }

        return { avgCallsPerWeek, avgTalkTime, callScore, talkTimeScore };
    };

    const toggleCalendar = () => {
        setShowCalendar(!showCalendar);
    };

    const handleSelectDateRange = (ranges) => {
        setSelectionRange(ranges.selection);
        toggleCalendar();
    };

    const fetchAgents = async () => {
        try {
            const response = await axios.get(`${API_ENDPOINT}/get-agent-data`);
            const agentOptions = (response.data || []).map(agent => ({
                value: agent.agent_number,
                label: agent.name || agent.agent_number
            }));
            // Add "All Agents" option
            const allAgentsOption = { value: 'all', label: 'All Agents' };
            setAgents([allAgentsOption, ...agentOptions]);
            // Set default selection to all agents
            setSelectedAgents([allAgentsOption]);
        } catch (error) {
            console.error('Error fetching agents:', error);
        }
    };

    const fetchCallData = async () => {
        // If "All Agents" is selected or no agents selected, use all agent values except 'all'
        const agentsToFetch = selectedAgents.some(agent => agent.value === 'all')
            ? agents.filter(agent => agent.value !== 'all')
            : selectedAgents;

        if (agentsToFetch.length === 0) {
            setData([]);
            return;
        }

        setLoading(true);
        try {
            // Format dates to ISO string and extract just the date part
            const startDate = selectionRange.startDate.toISOString().split('T')[0];
            const endDate = selectionRange.endDate.toISOString().split('T')[0];

            // Fetch data for selected agents in parallel
            const agentPromises = agentsToFetch.map(async (agent) => {
                try {
                    const response = await axios.get(`${API_ENDPOINT}/get-data`, {
                        params: {
                            agent_number: agent.value,
                            start_date: startDate,
                            end_date: endDate
                        }
                    });

                    // If no data for this agent, return null
                    if (!response.data || response.data.length === 0) {
                        return null;
                    }

                    // Calculate totals for this agent
                    const totalCalls = response.data.reduce((sum, call) => sum + (call.total_calls || 0), 0);
                    const totalDuration = response.data.reduce((sum, call) => sum + (call.duration || 0), 0);

                    const scores = calculateScores(totalCalls, totalDuration);
                    return {
                        agent_number: agent.value,
                        agent_name: agent.label,
                        ...scores,
                        qualityScore: qualityScores[agent.value] || 0,
                        totalScore: scores.callScore + scores.talkTimeScore + (qualityScores[agent.value] || 0)
                    };
                } catch (error) {
                    console.error(`Error fetching data for agent ${agent.value}:`, error);
                    return null; // Skip agents with errors
                }
            });

            const results = await Promise.all(agentPromises);
            // Filter out null results (agents with no data)
            setData(results.filter(result => result !== null));

            toast.success("Data fetched successfully!");
        } catch (error) {
            console.error('Error fetching call data:', error);
            toast.error('Failed to fetch call data');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (userInfo?.email) {
            fetchAgents();
        }
    }, [userInfo?.email]);

    useEffect(() => {
        fetchCallData();
    }, [selectedAgents]);

    useEffect(() => {
        if (userInfo?.email) {
            fetchCallData();
        }
    }, [userInfo?.email, selectedAgent, selectionRange]);

    const handleQualityScoreChange = (agentNumber, score) => {
        const newScore = parseInt(score);
        setQualityScores(prev => ({
            ...prev,
            [agentNumber]: newScore
        }));

        setData(prev => prev.map(row => {
            if (row.agent_number === agentNumber) {
                return {
                    ...row,
                    qualityScore: newScore,
                    totalScore: row.callScore + row.talkTimeScore + newScore
                };
            }
            return row;
        }));
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h}h ${m}m ${s}s`;
    };

    return (
        <div className="p-4 w-full">
            <Toaster
                position="bottom-left"
                toastOptions={{
                    style: {
                        background: "#ff8e00",
                        color: "#2e2c2d",
                    },
                    success: {
                        duration: 2000,
                    },
                }}
            />
            <Modal
                style={customStyles}
                appElement={document.getElementById('root')}
                isOpen={showCalendar}
                onRequestClose={toggleCalendar}
            >
                <DateRangePicker
                    ranges={[selectionRange]}
                    onChange={handleSelectDateRange}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    direction="horizontal"
                />
            </Modal>

            <h1 className="text-2xl font-bold text-center mb-6">Call Score</h1>

            <div className="flex justify-center mb-6">
                <div className="mr-4">
                    <button
                        className="bg-theme-yellow-light text-black rounded px-3 py-2 hover:bg-theme-yellow-dark"
                        onClick={toggleCalendar}
                    >
                        Select Date Range
                    </button>
                    <span className="ml-3">
                        {selectionRange.startDate.toLocaleDateString()} - {selectionRange.endDate.toLocaleDateString()}
                    </span>
                </div>

                <div className="w-1/3">
                    <Select
                        isMulti
                        name="agents"
                        options={agents}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Select Agents..."
                        value={selectedAgents}
                        onChange={(selected) => {
                            if (selected.some(option => option.value === 'all')) {
                                setSelectedAgents([agents.find(agent => agent.value === 'all')]);
                            } else {
                                setSelectedAgents(selected);
                            }
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : data.length === 0 ? (
                <p className="text-xl text-center mt-10">No Data Found for the Selected Filters.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                        <thead>
                            <tr>
                                {[
                                    'Agent Name',
                                    'Avg Calls/Week',
                                    'Call Score',
                                    'Avg Talk Time',
                                    'Talk Time Score',
                                    'Quality Score',
                                    'Total Score'
                                ].map(header => (
                                    <th
                                        key={header}
                                        className="py-2 px-4 border-b bg-[#EEAF50]"
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, index) => (
                                <tr key={index} className="text-center">
                                    <td className="py-2 px-4 border-b">{row.agent_name}</td>
                                    <td className="py-2 px-4 border-b">{row.avgCallsPerWeek}</td>
                                    <td className="py-2 px-4 border-b">{row.callScore}</td>
                                    <td className="py-2 px-4 border-b">
                                        {row.avgTalkTime} <br />
                                        ({formatTime(row.avgTalkTime)})
                                    </td>
                                    <td className="py-2 px-4 border-b">{row.talkTimeScore}</td>
                                    <td className="py-2 px-4 border-b">
                                        <select
                                            value={row.qualityScore}
                                            onChange={(e) => handleQualityScoreChange(row.agent_number, e.target.value)}
                                            className="border px-2 py-1 rounded"
                                        >
                                            {[0, 1, 2, 3, 4].map(score => (
                                                <option key={score} value={score}>{score}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="py-2 px-4 border-b">{row.totalScore}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}